import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { fmtDate } from './fleetUtils';

export const fmtTimeParis = (iso) => iso
  ? new Date(iso).toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' })
  : null;

export const CERTAINTY_CLS = {
  exact: 'bg-green-100 text-green-700',
  heure_declaree: 'bg-yellow-100 text-yellow-700',
  planifie: 'bg-blue-100 text-blue-700',
  jour: 'bg-gray-200 text-gray-600',
};

export const periodHours = (p) => {
  if ((p.precision || 'exacte') === 'exacte' && p.started_at) {
    return `${fmtTimeParis(p.started_at)} → ${p.ended_at ? fmtTimeParis(p.ended_at) : 'en cours'}`;
  }
  if (p.precision === 'heure_declaree') return `${p.start_time_declared || '?'} → ${p.end_time_declared || '?'} (déclaré)`;
  if (p.precision === 'planifie') return `${p.planned_start_time || '?'} → ${p.planned_end_time || '?'} (planifié)`;
  return 'Journée (heures inconnues)';
};

export const CertaintyBadge = ({ certainty }) => certainty ? (
  <span className={`text-xs px-2 py-0.5 rounded ${CERTAINTY_CLS[certainty.key] || 'bg-gray-200'}`}>{certainty.label}</span>
) : null;

const PeriodDetailDialog = ({ period, open, onClose }) => {
  if (!period) return null;
  const rows = [
    ['Date (Paris)', period.date],
    ['Horaires', periodHours(period)],
    ['Livreur', `${period.user_name || '—'}${period.user_deleted ? ' (compte supprimé — copie historique)' : ''}`],
    ['Site', period.site_name || period.site_id || 'Flotte commune'],
    ['Mission / planning', period.schedule_id || '—'],
    ['Plaque au moment des faits', period.plate_snapshot || period.plate_number],
    ['Véhicule (id)', period.vehicle_id || '—'],
    ['Kilométrage', `${period.km_debut ?? '—'} → ${period.km_fin ?? '—'}`],
    ['Début (UTC serveur)', period.started_at || '—'],
    ['Fin (UTC serveur)', period.ended_at || '—'],
    ['Motif de début', period.start_reason || '—'],
    ['Motif de fin', period.end_reason || '—'],
    ['Clôturé par', period.closed_by ? `${period.closed_by.name || period.closed_by.label || period.closed_by.type}` : '—'],
    ['Source', period.source || 'temps_reel'],
    ['Précision', period.certainty?.label || period.precision || 'Exact'],
  ];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="period-detail-dialog">
        <DialogHeader><DialogTitle>Période d'utilisation — {period.plate_snapshot || period.plate_number}</DialogTitle></DialogHeader>
        <div className="space-y-1">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 text-xs border-b py-1.5">
              <span className="text-gray-500">{k}</span><span className="font-medium text-right">{v}</span>
            </div>
          ))}
          {period.legal_hold && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2" data-testid="legal-hold-badge">
              🔒 Gel juridique actif — {period.legal_hold_reason} ({fmtDate(period.legal_hold_at)})
            </p>
          )}
          {period.corrected && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
              ✏️ Période corrigée (audit conservé) — dernière correction : {fmtDate(period.last_correction_at)}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DriverHistoryView = ({ fixedVehicleId = null, compact = false, sites = [] }) => {
  const [filters, setFilters] = useState({ date_from: '', date_to: '', user: '', site_id: '', plate: '', schedule_id: '' });
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const limit = compact ? 10 : 20;

  const load = useCallback(async (skip = 0, append = false) => {
    setLoading(true); setError(null);
    try {
      const params = { limit, skip };
      if (fixedVehicleId) params.vehicle_id = fixedVehicleId;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API}/fleet/usage/periods`, { params, withCredentials: true });
      setData(res.data);
      setItems(prev => append ? [...prev, ...res.data.items] : res.data.items);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de chargement');
    } finally { setLoading(false); }
  }, [filters, fixedVehicleId, limit]);

  useEffect(() => { const t = setTimeout(() => load(0, false), 400); return () => clearTimeout(t); }, [load]);

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  if (error) return <p className="text-sm text-red-600 border border-red-200 bg-red-50 rounded p-3" data-testid="driver-history-error">{error}</p>;

  return (
    <div className="space-y-3" data-testid="driver-history-view">
      <div className={`grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-5'}`}>
        <div><Label className="text-xs">Du</Label>
          <Input type="date" value={filters.date_from} onChange={(e) => setF('date_from', e.target.value)} data-testid="usage-filter-from" /></div>
        <div><Label className="text-xs">Au</Label>
          <Input type="date" value={filters.date_to} onChange={(e) => setF('date_to', e.target.value)} data-testid="usage-filter-to" /></div>
        <div><Label className="text-xs">Livreur</Label>
          <Input value={filters.user} onChange={(e) => setF('user', e.target.value)} placeholder="Nom" data-testid="usage-filter-user" /></div>
        {!compact && !fixedVehicleId && (
          <>
            <div><Label className="text-xs">Plaque</Label>
              <Input value={filters.plate} onChange={(e) => setF('plate', e.target.value)} placeholder="AB-123-CD" data-testid="usage-filter-plate" /></div>
            <div><Label className="text-xs">Site</Label>
              <select value={filters.site_id} onChange={(e) => setF('site_id', e.target.value)}
                className="w-full px-2 py-2 border rounded-md bg-white text-sm" data-testid="usage-filter-site">
                <option value="">Tous</option>
                {sites.map(s => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
              </select></div>
          </>
        )}
      </div>

      <p className="text-xs text-gray-500" data-testid="driver-history-total">{data ? `${data.total} période(s) d'utilisation` : 'Chargement...'}</p>

      <div className="space-y-1.5">
        {items.map(p => (
          <Card key={p.assignment_id} className="cursor-pointer hover:bg-gray-50" onClick={() => setDetail(p)}
            data-testid={`usage-row-${p.assignment_id}`}>
            <CardContent className="p-2.5 flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-medium">{p.date}</span>
                <span className="text-gray-600">{periodHours(p)}</span>
                <span className="font-semibold">{p.user_name || '—'}</span>
                <span className="text-gray-500">{p.site_name || p.site_id || 'Flotte commune'}</span>
                {p.schedule_id && <span className="text-gray-400">mission {String(p.schedule_id).slice(0, 10)}…</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold">{p.plate_snapshot || p.plate_number}</span>
                <CertaintyBadge certainty={p.certainty} />
                {p.legal_hold && <span title="Gel juridique">🔒</span>}
              </div>
            </CardContent>
          </Card>
        ))}
        {!loading && items.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Aucune période d'utilisation trouvée</p>}
      </div>

      {data && items.length < data.total && (
        <Button variant="outline" className="w-full" disabled={loading} onClick={() => load(items.length, true)}
          data-testid="usage-load-more">
          {loading ? 'Chargement...' : `Charger plus (${items.length}/${data.total})`}
        </Button>
      )}

      <PeriodDetailDialog period={detail} open={!!detail} onClose={() => setDetail(null)} />
    </div>
  );
};

export default DriverHistoryView;
